// App.jsx — Complete & Final Production-Ready Version
import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, FileText, Search, TrendingUp, AlertCircle,
  CheckCircle, XCircle, List, Filter, Globe, Users,
  Copy, RefreshCw, Calendar, Loader2
} from 'lucide-react';

const API_BASE_URL = 'https://icil-credit-reports-back.root.sx/api/v1';

export default function CreditDecisionApp() {
  const [activeTab, setActiveTab] = useState('upload');
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const fetchDocuments = useCallback(async (filter = '') => {
    setLoadingDocs(true);
    try {
      const url = filter
        ? `${API_BASE_URL}/list_documents?company_name=${encodeURIComponent(filter)}`
        : `${API_BASE_URL}/list_documents`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch documents');
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      alert('Could not load documents. Please try again.');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    if (['documents', 'query', 'decision', 'generic', 'portfolio'].includes(activeTab)) {
      fetchDocuments();
    }
  }, [activeTab, fetchDocuments]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <nav className="bg-white shadow-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <TrendingUp className="h-10 w-10 text-indigo-600" />
          <h1 className="ml-3 text-2xl font-extrabold text-gray-900">Credit Decision System</h1>
        </div>
      </nav>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap gap-1 p-2 bg-gray-50">
              {[
                { key: 'upload', icon: <Upload className="w-5 h-5" />, label: 'Upload PDF' },
                { key: 'documents', icon: <List className="w-5 h-5" />, label: 'Documents' },
                { key: 'query', icon: <Search className="w-5 h-5" />, label: 'Query (Single)' },
                { key: 'generic', icon: <Globe className="w-5 h-5" />, label: 'Global Search' },
                { key: 'decision', icon: <FileText className="w-5 h-5" />, label: 'Credit Decision' },
                { key: 'portfolio', icon: <Users className="w-5 h-5" />, label: 'Portfolio Overview' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                    activeTab === tab.key
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-8 min-h-screen">
            {activeTab === 'upload' && <UploadTab onSuccess={() => fetchDocuments()} />}
            {activeTab === 'documents' && <DocumentsTab documents={documents} loading={loadingDocs} onRefresh={fetchDocuments} />}
            {activeTab === 'query' && <SingleQueryTab documents={documents} />}
            {activeTab === 'generic' && <GenericQueryTab />}
            {activeTab === 'decision' && <SingleDecisionTab documents={documents} />}
            {activeTab === 'portfolio' && <PortfolioTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ==================== UPLOAD TAB ==================== */
function UploadTab({ onSuccess }) {
  const [file, setFile] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) return setError('Please select a PDF file');
    setUploading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    if (companyName.trim()) formData.append('company_name', companyName.trim());

    try {
      const res = await fetch(`${API_BASE_URL}/upload_pdf`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.detail || 'Upload failed');

      setResult(data);
      setFile(null);
      setCompanyName('');
      document.getElementById('file-input')?.value && (document.getElementById('file-input').value = '');
      onSuccess();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-8">Upload Financial Report</h2>
      <div className="space-y-6">
        <input
          type="text"
          placeholder="Company Name"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          className="w-full px-5 py-4 border border-gray-300 rounded-xl text-lg focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 outline-none"
          required
        />
        <div className="border-4 border-dashed border-gray-300 rounded-2xl p-12 text-center hover:border-indigo-500 transition cursor-pointer">
          <Upload className="w-20 h-20 mx-auto text-gray-400 mb-4" />
          <label className="cursor-pointer text-indigo-600 font-bold text-xl">
            Click to upload PDF
            <input
              id="file-input"
              type="file"
              accept=".pdf"
              onChange={e => setFile(e.target.files[0] || null)}
              className="hidden"
            />
          </label>
          {file && <p className="mt-4 font-medium text-gray-700">Selected: <span className="font-bold">{file.name}</span></p>}
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading || !file}
          className="w-full py-5 bg-indigo-600 text-white font-bold text-xl rounded-xl hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center gap-3"
        >
          {uploading ? (
            <>Processing... <Loader2 className="animate-spin" /></>
          ) : (
            'Upload & Analyze'
          )}
        </button>

        {error && (
          <div className="p-4 bg-red-100 border border-red-400 rounded-lg text-red-700 flex items-center gap-3">
            <XCircle className="w-6 h-6 flex-shrink-0" /> {error}
          </div>
        )}
        {result && (
          <div className="p-4 bg-green-100 border border-green-400 rounded-lg text-green-700 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 flex-shrink-0" />
            <div>
              {result.message || 'Upload successful!'} — Document ID: <code className="bg-green-200 px-2 py-1 rounded">{result.doc_id}</code>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== DOCUMENTS TAB ==================== */
function DocumentsTab({ documents, loading, onRefresh }) {
  const [filter, setFilter] = useState('');

  const handleFilter = () => onRefresh(filter);

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold">Uploaded Documents</h2>
        <button onClick={() => onRefresh()} className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
          <RefreshCw className="w-5 h-5" /> Refresh
        </button>
      </div>

      <div className="flex gap-4 mb-8">
        <input
          type="text"
          placeholder="Filter by company name..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && handleFilter()}
          className="flex-1 px-5 py-3 border border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-300 outline-none"
        />
        <button onClick={handleFilter} className="px-6 py-3 bg-gray-800 text-white rounded-xl flex items-center gap-2 hover:bg-gray-900">
          <Filter className="w-5 h-5" /> Filter
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <Loader2 className="inline-block animate-spin h-16 w-16 text-indigo-600" />
        </div>
      ) : documents.length === 0 ? (
        <p className="text-center text-gray-500 py-20 text-xl">No documents uploaded yet.</p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {documents.map(doc => (
            <div key={doc.doc_id} className="p-6 bg-gradient-to-br from-indigo-50 to-white rounded-xl border shadow hover:shadow-xl transition">
              <div className="flex items-start gap-4">
                <FileText className="w-12 h-12 text-indigo-600 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-gray-900">
                    {doc.company_name || 'Unknown Company'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">ID: {doc.doc_id.slice(0, 16)}...</p>
                  {doc.uploaded_at && (
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==================== SINGLE QUERY TAB ==================== */
function SingleQueryTab({ documents }) {
  const [query, setQuery] = useState('');
  const [docId, setDocId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [useDocId, setUseDocId] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (documents.length > 0 && useDocId && !docId) {
      setDocId(documents[0].doc_id);
    }
  }, [documents, useDocId, docId]);

  const run = async () => {
    if (!query.trim()) return alert('Please enter a query');
    if (useDocId && !docId) return alert('Please select a document');
    if (!useDocId && !companyName.trim()) return alert('Please enter a company name');

    setLoading(true);
    const params = new URLSearchParams({ q: query });
    if (useDocId) params.append('doc_id', docId);
    else params.append('company_name', companyName);

    try {
      const res = await fetch(`${API_BASE_URL}/query?${params.toString()}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
      }
    });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setResult(data);
    } catch {
      alert('Query failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Answer copied to clipboard!');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-8">Query a Specific Document</h2>

      <div className="flex gap-8 justify-center mb-6 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={useDocId} onChange={() => setUseDocId(true)} className="w-4 h-4" />
          <span>By Document ID</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={!useDocId} onChange={() => setUseDocId(false)} className="w-4 h-4" />
          <span>By Company Name</span>
        </label>
      </div>

      {useDocId ? (
        <select value={docId} onChange={e => setDocId(e.target.value)} className="w-full p-4 border border-gray-300 rounded-xl mb-6 focus:ring-4 focus:ring-indigo-300 outline-none">
          <option value="">Select document...</option>
          {documents.map(d => (
            <option key={d.doc_id} value={d.doc_id}>
              {d.company_name || 'Unknown'} ({d.doc_id.slice(0, 8)}...)
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          placeholder="Enter exact company name"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          className="w-full p-4 border border-gray-300 rounded-xl mb-6 focus:ring-4 focus:ring-indigo-300 outline-none"
        />
      )}

      <textarea
        rows={5}
        placeholder="e.g. What is the total revenue in 2024? What are the payment terms?"
        value={query}
        onChange={e => setQuery(e.target.value)}
        className="w-full p-5 border border-gray-300 rounded-xl text-lg focus:ring-4 focus:ring-indigo-300 outline-none resize-none"
      />

      <button
        onClick={run}
        disabled={loading}
        className="mt-6 w-full py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition flex items-center justify-center gap-3"
      >
        {loading ? <>Searching... <Loader2 className="animate-spin" /></> : 'Run Query'}
      </button>

      {result && (
        <div className="mt-10 p-8 bg-gray-50 rounded-2xl border">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-2xl font-bold">Answer</h3>
            <button onClick={() => copyToClipboard(result.answer)} className="text-gray-600 hover:text-indigo-600">
              <Copy className="w-5 h-5" />
            </button>
          </div>
          <p className="whitespace-pre-wrap text-lg text-gray-800">{result.answer || 'No answer returned'}</p>

          {result.sources?.length > 0 && (
            <div className="mt-8">
              <h4 className="font-bold text-lg mb-4">Sources ({result.sources.length})</h4>
              {result.sources.map((s, i) => (
                <div key={i} className="p-4 bg-white rounded-lg border mb-3">
                  <p className="font-medium text-indigo-700">{s.metadata?.company_name || 'Unknown Company'}</p>
                  <p className="text-sm text-gray-600 mt-1">Page {s.metadata?.page || 'N/A'}</p>
                  <p className="text-gray-800 mt-2 text-sm leading-relaxed">{s.page_content || s.snippet}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==================== GLOBAL QUERY TAB — FINAL WORKING VERSION ==================== */
function GenericQueryTab() {
  const [query, setQuery] = useState('');
  const [topK, setTopK] = useState(10);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
  if (!query.trim()) {
    alert("Please enter a query");
    return;
  }

  setLoading(true);
  setResult(null);

  try {
    const url =
      `${API_BASE_URL}/query/generic` +
      `?q=${encodeURIComponent(query.trim())}` +
      `&top_k=${topK}`;

    console.log("Calling:", url);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
      }
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Server error ${response.status}`);
    }

    const data = await response.json();
    setResult(data);

  } catch (err) {
    console.error("Global search failed:", err);
    alert("Global search failed:\n" + err.message);
  } finally {
    setLoading(false);
  }
};


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Answer copied!');
  };

  return (
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-4">Global Search Across All Reports</h2>
      <p className="text-center text-gray-600 mb-10">
        Ask anything: "Which company is investable?" • "Show companies with DBT over 60 days" • "Who should we reject?"
      </p>

      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-10 rounded-2xl shadow-lg">
        <textarea
          rows={5}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type your portfolio-wide question here..."
          className="w-full p-6 text-lg border-0 rounded-xl focus:ring-4 focus:ring-indigo-300 outline-none resize-none"
        />

        <div className="flex justify-between items-center mt-6">
          <div className="flex items-center gap-3 text-gray-700">
            <span>Return top</span>
            <input
              type="number"
              min={5}
              max={50}
              value={topK}
              onChange={(e) => setTopK(Math.max(5, Math.min(50, +e.target.value)))}
              className="w-20 px-3 py-2 border rounded-lg text-center"
            />
            <span>chunks</span>
          </div>

          <button
            onClick={run}
            disabled={loading}
            className="px-10 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition flex items-center gap-3"
          >
            {loading ? (
              <>Searching All Documents... <Loader2 className="animate-spin" /></>
            ) : (
              'Search Everything'
            )}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-12 p-10 bg-white rounded-2xl shadow-xl border">
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl font-bold">Answer</h3>
            <button onClick={() => copyToClipboard(result.answer)} className="text-gray-600 hover:text-indigo-600">
              <Copy className="w-5 h-5" />
            </button>
          </div>
          <div className="prose prose-lg max-w-none whitespace-pre-wrap text-gray-800">
            {result.answer || 'No answer generated.'}
          </div>

          {result.sources && result.sources.length > 0 && (
            <div className="mt-10">
              <h4 className="text-xl font-bold mb-5">Sources ({result.sources.length})</h4>
              <div className="grid gap-5 md:grid-cols-2">
                {result.sources.map((src, i) => (
                  <div key={i} className="p-5 bg-gray-50 rounded-xl border">
                    <p className="font-semibold text-indigo-700">
                      {src.metadata?.company_name || 'Unknown Company'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">Chunk {i + 1}</p>
                    <p className="text-gray-800 text-sm leading-relaxed">
                      {src.page_content || src.snippet || 'No content'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ==================== SINGLE DECISION TAB ==================== */
function SingleDecisionTab({ documents }) {
  const [docId, setDocId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [useDocId, setUseDocId] = useState(true);
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    if (documents.length > 0 && useDocId && !docId) {
      setDocId(documents[0].doc_id);
    }
  }, [documents, useDocId, docId]);

  const getDecision = async () => {
    if (useDocId && !docId) return alert('Select a document');
    if (!useDocId && !companyName.trim()) return alert('Enter company name');

    const params = new URLSearchParams();
    useDocId ? params.append('doc_id', docId) : params.append('company_name', companyName);

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/decision?${params}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDecision(data);
    } catch {
      alert('Failed to get decision');
    } finally {
      setLoading(false);
    }
  };

  const badge = (text, color) => (
    <span className={`px-6 py-3 rounded-full font-bold text-lg ${color}`}>{text}</span>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-10">Credit Decision for One Company</h2>

      <div className="flex gap-8 justify-center mb-8 text-sm">
        <label className="flex items-center gap-2"><input type="radio" checked={useDocId} onChange={() => setUseDocId(true)} /> Document ID</label>
        <label className="flex items-center gap-2"><input type="radio" checked={!useDocId} onChange={() => setUseDocId(false)} /> Company Name</label>
      </div>

      {useDocId ? (
        <select value={docId} onChange={e => setDocId(e.target.value)} className="w-full p-4 border border-gray-300 rounded-xl mb-8 focus:ring-4 focus:ring-indigo-300 outline-none">
          <option value="">Select document...</option>
          {documents.map(d => (
            <option key={d.doc_id} value={d.doc_id}>
              {d.company_name || 'Unknown'} ({d.doc_id.slice(0, 8)}...)
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          placeholder="Enter exact company name"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          className="w-full p-4 border border-gray-300 rounded-xl mb-8 focus:ring-4 focus:ring-indigo-300 outline-none"
        />
      )}

      <button
        onClick={getDecision}
        disabled={loading || (useDocId && !docId) || (!useDocId && !companyName)}
        className="w-full py-5 bg-indigo-600 text-white font-bold text-xl rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition flex items-center justify-center gap-3"
      >
        {loading ? <>Analyzing... <Loader2 className="animate-spin" /></> : 'Get Credit Decision'}
      </button>

      {decision && (
        <div className="mt-12 space-y-8">
          <div className={`p-10 rounded-2xl text-white text-center ${decision.decision === 'APPROVE' ? 'bg-green-600' : decision.decision === 'REJECT' ? 'bg-red-600' : 'bg-yellow-600'}`}>
            <h3 className="text-5xl font-bold">{decision.decision || 'UNKNOWN'}</h3>
            <p className="text-2xl mt-4">{decision.company_name || 'Unknown Company'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-50 rounded-xl text-center">
              <p className="text-gray-600">Risk Level</p>
              <p className="text-3xl font-bold text-gray-900">{decision.risk_level || 'N/A'}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl text-center">
              <p className="text-gray-600">Recommended Limit</p>
              <p className="text-3xl font-bold text-gray-900">${Number(decision.recommended_credit_limit || 0).toLocaleString()}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl text-center">
              <p className="text-gray-600">Confidence</p>
              <p className="text-3xl font-bold text-gray-900">{decision.confidence ? decision.confidence + '%' : 'N/A'}</p>
            </div>
          </div>

          {decision.red_flags?.length > 0 && (
            <div className="p-8 bg-red-50 border-2 border-red-300 rounded-2xl">
              <h4 className="text-2xl font-bold text-red-800 mb-4 flex items-center gap-3">
                <AlertCircle className="w-8 h-8" /> Red Flags ({decision.red_flags.length})
              </h4>
              <ul className="space-y-2">
                {decision.red_flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-red-700">
                    <span>•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-8 bg-blue-50 rounded-2xl">
            <h4 className="text-2xl font-bold text-blue-900 mb-4">Executive Summary</h4>
            <p className="text-lg text-blue-800 leading-relaxed">{decision.summary || 'No summary available.'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== PORTFOLIO OVERVIEW TAB ==================== */
function PortfolioTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/decisions/all`)
      .then(r => r.json())
      .then(setData)
      .catch(() => alert('Failed to load portfolio data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center py-32">
        <Loader2 className="inline-block animate-spin h-20 w-20 text-indigo-600" />
        <p className="mt-6 text-xl text-gray-600">Loading portfolio...</p>
      </div>
    );
  }

  if (!data) return <p className="text-center py-20 text-red-600">Failed to load portfolio data.</p>;

  const riskColor = level => {
    const map = {
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
      MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      LOW: 'bg-green-100 text-green-800 border-green-300'
    };
    return map[level] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div>
      <h2 className="text-4xl font-bold text-center mb-12">Portfolio Risk Dashboard</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
        {[
          { label: 'Approved', value: data.summary?.approve || 0, color: 'bg-green-500' },
          { label: 'Rejected', value: data.summary?.reject || 0, color: 'bg-red-500' },
          { label: 'Manual Review', value: data.summary?.manual_review || 0, color: 'bg-yellow-500' },
          { label: 'Critical Risk', value: data.summary?.critical_risk || 0, color: 'bg-red-600' },
        ].map(item => (
          <div key={item.label} className="p-8 bg-white rounded-2xl shadow-lg text-center border-2">
            <div className={`w-20 h-20 mx-auto rounded-full ${item.color} flex items-center justify-center text-white text-3xl font-bold`}>
              {item.value}
            </div>
            <p className="mt-4 text-xl font-semibold text-gray-700">{item.label}</p>
          </div>
        ))}
      </div>

      <h3 className="text-2xl font-bold mb-6">All Companies</h3>
      <div className="space-y-6">
        {data.decisions?.map(d => (
          <div key={d.doc_id} className={`p-8 rounded-2xl border-4 ${riskColor(d.risk_level)}`}>
            <div className="flex justify-between items-center">
              <div>
                <h4 className="text-2xl font-bold text-gray-900">{d.company_name || 'Unknown'}</h4>
                <p className="text-lg opacity-80">Recommended Limit: ${Number(d.recommended_credit_limit || 0).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <span className={`px-6 py-3 rounded-full font-bold text-white text-lg ${
                  d.decision === 'APPROVE' ? 'bg-green-600' :
                  d.decision === 'REJECT' ? 'bg-red-600' : 'bg-yellow-600'
                }`}>
                  {d.decision}
                </span>
                <p className="text-2xl font-bold mt-3">{d.risk_level || 'Unknown'}</p>
                {d.red_flags_count > 0 && <p className="text-sm mt-2 font-medium">Red Flags: {d.red_flags_count}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}